/**
 * SOP Detail Page
 *
 * Detailed view for a single SOP document with hierarchical Jobs/Tasks/Steps structure.
 * Two-column layout: metadata/PDF on left, hierarchical structure on right.
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  FileText,
  Building2,
  Calendar,
  Clock,
  Shield,
  ShieldAlert,
  Plus,
  Trash2,
  Save,
  Download,
  Loader2,
  CheckCircle2,
  XCircle,
  GripVertical,
  FileDown,
  BarChart3,
  Eye,
  ChevronDown,
  ChevronRight,
  ListChecks,
  AlertTriangle,
  Clock3,
  Sparkles,
  PencilRuler,
  Activity,
} from 'lucide-react';
import { useSop, useUpdateSop, useUpdateSopSteps } from '../../hooks/useSOPs';
import { useRoleLevel } from '../../lib/hooks/useRoleLevel';
import type { SopStepInput, SopDetailSection, SopSectionTask } from '@guild/shared';
import { useDocumentTitle } from '../../lib/hooks/useDocumentTitle';
import { FallbackEditorModal } from './components/FallbackEditorModal';
// Phase 23.3.3 — SOP Health tab (lazy mounted; only renders the network
// call when the user switches to the Health tab, keeping the Structure
// tab the zero-added-latency default).
import { SopHealthTab } from './components/SopHealthTab';

// Phase 23.1.6 — master kill switch for the fallback editor menu item.
// Read inline (not fetched from /api/config) per the MVP acceptance in the
// task brief. Flip to false if the editor surface needs a code-deploy hide.
const MANUAL_EDITOR_ENABLED = true;

interface EditableStep extends SopStepInput {
  id?: string;
  tempId?: string;
}

export function SOPDetailPage() {
  useDocumentTitle('SOP Detail');
  const { id } = useParams<{ id: string }>();

  // Role gating for the "Edit (advanced)" fallback editor button. Trainer (4)
  // and above see it; manager (3) does not.
  // Phase 19.S frontend migration (May 6, 2026) — role flows from
  // DB-canonical /api/me via useRoleLevel, not the JWT claim.
  const { isTrainerOrHigher } = useRoleLevel();
  const canUseFallbackEditor = MANUAL_EDITOR_ENABLED && isTrainerOrHigher;

  // Data fetching
  const { data: sop, isLoading, error } = useSop(id ?? null);

  // Mutations
  const updateSop = useUpdateSop();
  const updateSteps = useUpdateSopSteps();

  // Local state for legacy steps editing
  const [editedSteps, setEditedSteps] = useState<EditableStep[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  // State for hierarchical view (expanded sections and tasks)
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set());

  // Phase 23.1.6 — fallback editor modal open state
  const [editorOpen, setEditorOpen] = useState(false);

  // Phase 23.3.3 — right-column tab selection. Default 'structure' keeps
  // the existing Jobs tree visible on initial load; 'health' lazy-mounts
  // the SopHealthTab component so the /health query only fires when the
  // user opts in.
  const [activeTab, setActiveTab] = useState<'structure' | 'health'>(
    'structure'
  );

  // Initialize steps from fetched data
  useEffect(() => {
    if (sop?.steps) {
      setEditedSteps(
        sop.steps.map((step, index) => ({
          ...step,
          tempId: `step-${index}-${Date.now()}`,
        }))
      );
      setHasChanges(false);
    }
  }, [sop?.steps]);

  // Step management
  const handleStepChange = useCallback(
    (index: number, field: keyof EditableStep, value: unknown) => {
      setEditedSteps((prev) =>
        prev.map((step, i) => (i === index ? { ...step, [field]: value } : step))
      );
      setHasChanges(true);
    },
    []
  );

  const handleAddStep = useCallback(() => {
    setEditedSteps((prev) => [
      ...prev,
      {
        tempId: `new-step-${Date.now()}`,
        stepNumber: prev.length + 1,
        description: '',
        isSafetyCritical: false,
        keywords: [],
      },
    ]);
    setHasChanges(true);
  }, []);

  const handleRemoveStep = useCallback((index: number) => {
    setEditedSteps((prev) =>
      prev
        .filter((_, i) => i !== index)
        .map((step, i) => ({ ...step, stepNumber: i + 1 }))
    );
    setHasChanges(true);
  }, []);

  const handleMoveStep = useCallback((fromIndex: number, direction: 'up' | 'down') => {
    const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;
    setEditedSteps((prev) => {
      if (toIndex < 0 || toIndex >= prev.length) return prev;
      const newSteps = [...prev];
      [newSteps[fromIndex], newSteps[toIndex]] = [newSteps[toIndex], newSteps[fromIndex]];
      return newSteps.map((step, i) => ({ ...step, stepNumber: i + 1 }));
    });
    setHasChanges(true);
  }, []);

  // Save changes
  const handleSave = async () => {
    if (!id || !hasChanges) return;

    setSaveStatus('saving');
    try {
      await updateSteps.mutateAsync({
        id,
        data: {
          steps: editedSteps.map((step) => ({
            stepNumber: step.stepNumber,
            description: step.description,
            expectedDuration: step.expectedDuration,
            isSafetyCritical: step.isSafetyCritical,
            keywords: step.keywords,
          })),
        },
      });
      setSaveStatus('saved');
      setHasChanges(false);
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (err) {
      console.error('Save error:', err);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  };

  // Toggle active status
  const handleToggleActive = async () => {
    if (!id || !sop) return;

    try {
      await updateSop.mutateAsync({
        id,
        data: { isActive: !sop.isActive },
      });
    } catch (err) {
      console.error('Toggle active error:', err);
    }
  };

  // Toggle section expansion
  const toggleSection = useCallback((sectionId: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  }, []);

  // Toggle task expansion
  const toggleTask = useCallback((taskId: string) => {
    setExpandedTasks((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  }, []);

  // Expand all sections
  const expandAllSections = useCallback(() => {
    if (sop?.sections) {
      setExpandedSections(new Set(sop.sections.map((s) => s.id)));
    }
  }, [sop?.sections]);

  // Collapse all sections
  const collapseAllSections = useCallback(() => {
    setExpandedSections(new Set());
    setExpandedTasks(new Set());
  }, []);

  // Format date
  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-[#00674a] flex items-center justify-center animate-pulse">
            <FileText className="w-8 h-8 text-white" />
          </div>
          <p className="text-gray-500 font-medium">Loading SOP...</p>
        </div>
      </div>
    );
  }

  // Error/Not found state
  if (error || !sop) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-6">
            <FileText className="w-10 h-10 text-gray-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">SOP Not Found</h1>
          <p className="text-gray-500 mb-8">
            The document you're looking for doesn't exist or you don't have access to it.
          </p>
          <Link
            to="/sops"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#00674a] text-white font-semibold rounded-xl hover:bg-[#005540] transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to SOPs
          </Link>
        </div>
      </div>
    );
  }

  const sopData = sop;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Back navigation */}
        <Link
          to="/sops"
          className="inline-flex items-center gap-2 text-gray-500 hover:text-[#00674a] transition-colors mb-6 group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          <span className="font-medium">Back to SOPs</span>
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:items-stretch">
          {/* Left Column - Metadata */}
          <div className="lg:col-span-2 flex flex-col gap-6 lg:gap-0 lg:justify-between">
            {/* Title card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#00674a] to-[#00674a]/80 flex items-center justify-center flex-shrink-0 shadow-lg shadow-[#00674a]/20">
                  <FileText className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h1 className="text-2xl font-bold text-gray-900 tracking-tight leading-tight">
                    {sopData.title}
                  </h1>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <span className="px-3 py-1 bg-gray-100 rounded-full text-sm font-semibold text-gray-600">
                      v{sopData.version}
                    </span>
                    <button
                      onClick={handleToggleActive}
                      disabled={updateSop.isPending}
                      className={`
                        px-3 py-1 rounded-full text-sm font-semibold transition-all
                        ${
                          sopData.isActive
                            ? 'bg-[#c5e8c2] text-[#00674a]'
                            : 'bg-gray-100 text-gray-500'
                        }
                        hover:scale-105 disabled:opacity-50
                      `}
                    >
                      {sopData.isActive ? 'Active' : 'Inactive'}
                    </button>
                  </div>
                </div>
              </div>
              {/* Phase 23.1.6 — Edit (advanced) entry point for trainers+.
                  Opens the fallback editor modal which creates a manual
                  sop_draft_proposals row (source='manual'). Hidden for
                  manager role and below. */}
              {canUseFallbackEditor && (
                <button
                  type="button"
                  onClick={() => setEditorOpen(true)}
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 px-3 py-2 border border-gray-200 rounded-lg text-sm font-semibold text-gray-700 hover:border-[#00674a]/40 hover:bg-[#c5e8c2]/20 transition-colors"
                  title="Create a trainer-authored proposal (advanced). Submits to the proposal queue for four-eyes approval."
                >
                  <PencilRuler className="w-4 h-4 text-gray-500" />
                  Edit (advanced)
                </button>
              )}
            </div>

            {/* Metadata card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-4">
              <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">
                Document Info
              </h3>

              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <Building2 className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-400 font-medium">Department</p>
                    <p className="text-gray-900 font-semibold">
                      {sopData.department?.name || 'All Departments'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <BarChart3 className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-400 font-medium">Assessments</p>
                    <p className="text-gray-900 font-semibold">
                      {sopData.assessmentCount} completed
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <Calendar className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-400 font-medium">Created</p>
                      <p className="text-gray-900 font-semibold text-sm">
                        {formatDate(sopData.createdAt)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <Clock className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-400 font-medium">Updated</p>
                      <p className="text-gray-900 font-semibold text-sm">
                        {formatDate(sopData.updatedAt)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* PDF Download card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">
                Source Document
              </h3>
              <div className="flex items-start gap-4">
                <div className="w-14 h-16 bg-red-50 rounded-lg flex items-center justify-center border border-red-100 flex-shrink-0">
                  <FileDown className="w-6 h-6 text-red-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p
                    className="font-semibold text-gray-900 truncate text-sm"
                    title={sopData.s3Key?.split('/').pop() || 'document.pdf'}
                  >
                    {sopData.s3Key?.split('/').pop() || 'document.pdf'}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">PDF Document</p>
                  <div className="flex items-center gap-2 mt-3">
                    <a
                      href={sopData.downloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-1.5 bg-guild-green hover:bg-guild-green/90 rounded-lg font-semibold text-white text-sm transition-colors flex items-center gap-1.5"
                    >
                      <Eye className="w-3.5 h-3.5" />
                      View
                    </a>
                    <a
                      href={sopData.downloadUrl}
                      download
                      className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg font-semibold text-gray-700 text-sm transition-colors flex items-center gap-1.5"
                    >
                      <Download className="w-3.5 h-3.5" />
                      Download
                    </a>
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* Phase 23.1.6 — Fallback editor modal. Only rendered when
              `editorOpen` is true so acquire-lock fires on explicit open. */}
          {editorOpen && canUseFallbackEditor && (
            <FallbackEditorModal
              sop={sopData}
              onClose={() => setEditorOpen(false)}
            />
          )}

          {/* Right Column - Hierarchical Jobs View + Health tab (Phase 23.3.3) */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              {/* Tab bar (Phase 23.3.3 — Structure + Health).
                  Keeps the same card envelope; swaps body based on the
                  `activeTab` state above. ARIA pattern matches
                  ProposalsInboxPage.tsx for consistency across the feature. */}
              <div
                className="px-6 pt-4 border-b border-gray-100 flex items-center gap-1"
                role="tablist"
                aria-label="SOP detail tabs"
              >
                <button
                  type="button"
                  role="tab"
                  id="sop-tab-structure"
                  aria-selected={activeTab === 'structure'}
                  aria-controls="sop-tabpanel-structure"
                  onClick={() => setActiveTab('structure')}
                  className={`px-4 py-2 text-sm font-semibold rounded-t-lg transition-colors ${
                    activeTab === 'structure'
                      ? 'text-[#00674a] bg-[#c5e8c2]/30 border-b-2 border-[#00674a]'
                      : 'text-gray-500 hover:text-[#00674a] border-b-2 border-transparent'
                  }`}
                >
                  <span className="inline-flex items-center gap-2">
                    <ListChecks className="w-4 h-4" />
                    Structure
                  </span>
                </button>
                <button
                  type="button"
                  role="tab"
                  id="sop-tab-health"
                  aria-selected={activeTab === 'health'}
                  aria-controls="sop-tabpanel-health"
                  onClick={() => setActiveTab('health')}
                  className={`px-4 py-2 text-sm font-semibold rounded-t-lg transition-colors ${
                    activeTab === 'health'
                      ? 'text-[#00674a] bg-[#c5e8c2]/30 border-b-2 border-[#00674a]'
                      : 'text-gray-500 hover:text-[#00674a] border-b-2 border-transparent'
                  }`}
                >
                  <span className="inline-flex items-center gap-2">
                    <Activity className="w-4 h-4" />
                    Health
                  </span>
                </button>
              </div>

              {activeTab === 'structure' ? (
                <div
                  role="tabpanel"
                  id="sop-tabpanel-structure"
                  aria-labelledby="sop-tab-structure"
                >
                  {/* Jobs header */}
                  <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">Procedural Jobs</h2>
                      <p className="text-sm text-gray-500 mt-1">
                        {sopData.jobCount ?? 0} {(sopData.jobCount ?? 0) === 1 ? 'job' : 'jobs'} defined
                      </p>
                    </div>
                    {(sopData.sections?.length ?? 0) > 0 && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={expandAllSections}
                          className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-[#00674a] hover:bg-[#c5e8c2]/20 rounded-lg transition-colors"
                        >
                          Expand All
                        </button>
                        <button
                          onClick={collapseAllSections}
                          className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-[#00674a] hover:bg-[#c5e8c2]/20 rounded-lg transition-colors"
                        >
                          Collapse All
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Jobs list (hierarchical sections → tasks → steps) */}
                  <div className="p-6 space-y-3 max-h-[700px] overflow-y-auto">
                    {(sopData.sections?.length ?? 0) > 0 ? (
                      sopData.sections?.map((section, sectionIndex) => (
                        <JobCard
                          key={section.id}
                          section={section}
                          sectionIndex={sectionIndex}
                          isExpanded={expandedSections.has(section.id)}
                          expandedTasks={expandedTasks}
                          onToggleSection={() => toggleSection(section.id)}
                          onToggleTask={toggleTask}
                        />
                      ))
                    ) : editedSteps.length > 0 ? (
                      // Fallback: Show legacy flat steps for SOPs that haven't been processed
                      <LegacyStepsEditor
                        steps={editedSteps}
                        hasChanges={hasChanges}
                        saveStatus={saveStatus}
                        onStepChange={handleStepChange}
                        onAddStep={handleAddStep}
                        onRemoveStep={handleRemoveStep}
                        onMoveStep={handleMoveStep}
                        onSave={handleSave}
                      />
                    ) : (
                      <EmptyJobsState />
                    )}
                  </div>

                  {/* Status footer */}
                  {(sopData.sections?.length ?? 0) > 0 && (
                    <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                      <div className="flex items-center justify-between text-sm text-gray-500">
                        <div className="flex items-center gap-4">
                          <span>
                            <span className="font-semibold text-gray-700">{sopData.taskCount ?? 0}</span> tasks
                          </span>
                          <span>
                            <span className="font-semibold text-gray-700">{sopData.stepCount ?? 0}</span> steps
                          </span>
                        </div>
                        {sopData.processingStatus === 'completed' && (
                          <span className="inline-flex items-center gap-1.5 text-emerald-600">
                            <CheckCircle2 className="w-4 h-4" />
                            Processed
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                /* Phase 23.3.3 — Health tab body. Lazy-mount so the network
                   call only fires when the user switches to this tab. */
                <div
                  role="tabpanel"
                  id="sop-tabpanel-health"
                  aria-labelledby="sop-tab-health"
                  className="p-6 max-h-[760px] overflow-y-auto"
                >
                  {id && <SopHealthTab sopId={id} />}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SOPDetailPage;

// ============================================
// Job Card Component (Section = Job in UI)
// ============================================

interface JobCardProps {
  section: SopDetailSection;
  sectionIndex: number;
  isExpanded: boolean;
  expandedTasks: Set<string>;
  onToggleSection: () => void;
  onToggleTask: (taskId: string) => void;
}

function JobCard({
  section,
  sectionIndex,
  isExpanded,
  expandedTasks,
  onToggleSection,
  onToggleTask,
}: JobCardProps) {
  const hasSafetyTasks = section.tasks?.some((t) => t.isSafetyCritical) ?? false;

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden transition-all hover:border-[#00674a]/30">
      {/* Section header (clickable) */}
      <button
        onClick={onToggleSection}
        className="w-full px-4 py-3.5 bg-gray-50 hover:bg-gray-100 transition-colors flex items-center gap-3 text-left"
      >
        {/* Expand/collapse icon */}
        <div className="text-gray-400">
          {isExpanded ? (
            <ChevronDown className="w-5 h-5" />
          ) : (
            <ChevronRight className="w-5 h-5" />
          )}
        </div>

        {/* Section number badge */}
        <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-[#00674a] text-white flex items-center justify-center font-bold text-sm shadow-sm">
          {sectionIndex + 1}
        </div>

        {/* Section title */}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 truncate">{section.title}</h3>
          {section.description && (
            <p className="text-xs text-gray-500 truncate mt-0.5">{section.description}</p>
          )}
        </div>

        {/* Indicators */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {hasSafetyTasks && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 text-red-600 rounded text-xs font-medium">
              <ShieldAlert className="w-3 h-3" />
            </span>
          )}
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white border border-gray-200 rounded-lg text-xs font-semibold text-gray-600">
            <ListChecks className="w-3.5 h-3.5 text-gray-400" />
            {section.taskCount} {section.taskCount === 1 ? 'task' : 'tasks'}
          </span>
        </div>
      </button>

      {/* Phase 23.1.3 — When-to-do trigger row.
           Always rendered below the section header so trainers can see
           cadence without expanding the section. The "AI suggest" button is
           stubbed (disabled) until Chunk 23.2 wires the proposal inbox. */}
      <WhenToDoTriggerRow
        trigger={section.whenToDoTrigger ?? null}
        triggerSource={section.whenToDoTriggerSource ?? null}
      />

      {/* Expanded content: tasks list */}
      {isExpanded && section.tasks && section.tasks.length > 0 && (
        <div className="border-t border-gray-100">
          <div className="divide-y divide-gray-50">
            {section.tasks.map((task, taskIndex) => (
              <TaskItem
                key={task.id}
                task={task}
                taskIndex={taskIndex}
                isExpanded={expandedTasks.has(task.id)}
                onToggle={() => onToggleTask(task.id)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// When-to-do Trigger Row (Phase 23.1.3)
// ============================================

interface WhenToDoTriggerRowProps {
  trigger: string | null;
  triggerSource: string | null;
}

function WhenToDoTriggerRow({ trigger, triggerSource }: WhenToDoTriggerRowProps) {
  const hasTrigger = trigger !== null && trigger.trim().length > 0;
  const isTrainerApproved = triggerSource === 'trainer_approved';

  return (
    <div className="px-4 py-2 bg-white border-t border-gray-100 flex items-center justify-between gap-3">
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <Clock3
          className={`w-3.5 h-3.5 flex-shrink-0 ${hasTrigger ? 'text-[#00674a]' : 'text-gray-300'}`}
        />
        {hasTrigger ? (
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium truncate ${
              isTrainerApproved
                ? 'bg-[#c5e8c2] text-[#00674a]'
                : 'bg-gray-100 text-gray-700'
            }`}
            title={
              isTrainerApproved
                ? `When to do: ${trigger} (trainer-approved)`
                : `When to do: ${trigger}`
            }
          >
            <span className="truncate">{trigger}</span>
            {isTrainerApproved && (
              <CheckCircle2 className="w-3 h-3 flex-shrink-0" aria-label="Trainer approved" />
            )}
          </span>
        ) : (
          <span className="text-xs font-medium text-slate-400 italic">
            No trigger defined
          </span>
        )}
      </div>
      {/* Stubbed "AI suggest" button — disabled until Chunk 23.2 ships the
          proposal inbox. Click handler is intentionally omitted; the button
          is display-only for now. */}
      <button
        type="button"
        disabled
        title="Available when proposal inbox ships — Phase 23.2"
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold text-gray-400 bg-gray-50 border border-gray-200 cursor-not-allowed flex-shrink-0"
      >
        <Sparkles className="w-3 h-3" />
        AI suggest
      </button>
    </div>
  );
}

// ============================================
// Task Item Component
// ============================================

interface TaskItemProps {
  task: SopSectionTask;
  taskIndex: number;
  isExpanded: boolean;
  onToggle: () => void;
}

function TaskItem({ task, taskIndex, isExpanded, onToggle }: TaskItemProps) {
  // Phase 36.7 — click-to-expand for step images (lightbox copied from RedlineStep.tsx).
  // Tracks the step whose image is open rather than the URL itself, so the src stays
  // narrowed to a string without an assertion.
  const [expandedStepId, setExpandedStepId] = useState<string | null>(null);
  const expandedStep = expandedStepId
    ? task.steps?.find((s) => s.id === expandedStepId)
    : undefined;

  // Phase 36.7 — a step's image is the whole rendered page PNG of its parent task's
  // startPage, so every step in a single-page task points at the same page. Render each
  // distinct page once, on the first step that references it, instead of repeating an
  // identical image 3-5 times inside one expanded task. Deduping on pageNumber rather
  // than on imageUrl because the API presigns per step, so two steps sharing a page can
  // get different URL strings when the presign batch straddles a clock second. Legacy
  // pre-15.7 steps carry per-step embedded crops and have no pageNumber — those are
  // genuinely distinct images and are never deduped.
  const imageStepIds = new Set<string>();
  const seenPageNumbers = new Set<number>();
  for (const step of task.steps ?? []) {
    if (!step.imageUrl) continue;
    if (step.pageNumber == null) {
      imageStepIds.add(step.id);
      continue;
    }
    if (seenPageNumbers.has(step.pageNumber)) continue;
    seenPageNumbers.add(step.pageNumber);
    imageStepIds.add(step.id);
  }

  return (
    <div className="bg-white">
      {/* Task header */}
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 hover:bg-gray-50/50 transition-colors flex items-center gap-3 text-left"
      >
        {/* Indent spacer + expand icon */}
        <div className="flex items-center gap-2 pl-6">
          <div className="text-gray-300">
            {task.steps && task.steps.length > 0 ? (
              isExpanded ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )
            ) : (
              <div className="w-4 h-4" />
            )}
          </div>
        </div>

        {/* Task number */}
        <div className="flex-shrink-0 w-6 h-6 rounded bg-gray-100 text-gray-600 flex items-center justify-center font-semibold text-xs">
          {taskIndex + 1}
        </div>

        {/* Task title */}
        <div className="flex-1 min-w-0">
          <span className="text-sm text-gray-800">{task.title}</span>
        </div>

        {/* Indicators */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {task.isSafetyCritical && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-red-50 text-red-600 rounded text-xs font-medium">
              <ShieldAlert className="w-3 h-3" />
              Safety
            </span>
          )}
          {task.steps && task.steps.length > 0 && (
            <span className="text-xs text-gray-400">
              {task.steps.length} {task.steps.length === 1 ? 'step' : 'steps'}
            </span>
          )}
        </div>
      </button>

      {/* Expanded content: steps list */}
      {isExpanded && task.steps && task.steps.length > 0 && (
        <div className="pb-3 pl-20 pr-4">
          <div className="bg-gray-50 rounded-lg p-3 space-y-2">
            {task.steps.map((step, stepIndex) => (
              <div key={step.id} className="flex items-start gap-2 text-sm">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#00674a]/10 text-[#00674a] flex items-center justify-center text-xs font-semibold">
                  {stepIndex + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-2">
                    <p className="text-gray-700 leading-relaxed">{step.description}</p>
                    {step.isSafetyCritical && (
                      <ShieldAlert className="w-3.5 h-3.5 text-red-500 flex-shrink-0 mt-0.5" />
                    )}
                  </div>
                  {step.imageUrl && imageStepIds.has(step.id) && (
                    <div className="mt-2 rounded-lg border border-gray-200 overflow-hidden max-w-md">
                      <button
                        type="button"
                        onClick={() => setExpandedStepId(step.id)}
                        className="block w-full focus:outline-none focus:ring-2 focus:ring-[#00674a]/20"
                        aria-label={step.imageDescription || 'Open step image'}
                      >
                        <img
                          src={step.imageUrl}
                          alt={step.imageDescription || `Step ${stepIndex + 1} reference image`}
                          className="w-full h-auto max-h-64 object-contain bg-white"
                          loading="lazy"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      </button>
                      {step.imageDescription && (
                        <p className="text-xs text-gray-500 p-2 bg-gray-50">
                          {step.imageDescription}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Phase 36.7 — full-size page view. A page rendered into the 448px card is
          ~52 DPI; this is where it becomes readable. */}
      {expandedStep?.imageUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          role="dialog"
          aria-modal="true"
          onClick={() => setExpandedStepId(null)}
        >
          <div
            className="relative inline-block"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={expandedStep.imageUrl}
              alt={expandedStep.imageDescription || 'Step reference image'}
              className="block max-h-[90vh] max-w-[90vw] rounded-lg object-contain"
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// Empty Jobs State
// ============================================

function EmptyJobsState() {
  return (
    <div className="text-center py-12">
      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-50 to-amber-100 flex items-center justify-center mx-auto mb-4">
        <AlertTriangle className="w-8 h-8 text-amber-500" />
      </div>
      <h3 className="text-lg font-bold text-gray-900 mb-2">No Jobs Defined</h3>
      <p className="text-gray-500 mb-6 max-w-sm mx-auto">
        Process this SOP to extract jobs, tasks, and steps from the PDF document.
      </p>
    </div>
  );
}

// ============================================
// Legacy Steps Editor (for backward compatibility)
// ============================================

interface LegacyStepsEditorProps {
  steps: EditableStep[];
  hasChanges: boolean;
  saveStatus: 'idle' | 'saving' | 'saved' | 'error';
  onStepChange: (index: number, field: keyof EditableStep, value: unknown) => void;
  onAddStep: () => void;
  onRemoveStep: (index: number) => void;
  onMoveStep: (fromIndex: number, direction: 'up' | 'down') => void;
  onSave: () => void;
}

function LegacyStepsEditor({
  steps,
  hasChanges,
  saveStatus,
  onStepChange,
  onAddStep,
  onRemoveStep,
  onMoveStep,
  onSave,
}: LegacyStepsEditorProps) {
  return (
    <div className="space-y-4">
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-800">Legacy Steps View</p>
            <p className="text-xs text-amber-700 mt-0.5">
              This SOP has not been processed. Process it to see hierarchical jobs, tasks, and steps.
            </p>
          </div>
        </div>
      </div>

      {steps.map((step, index) => (
        <div
          key={step.tempId || step.id || index}
          className="group relative bg-gray-50 rounded-xl p-4 border border-gray-100 hover:border-[#00674a]/20 transition-all"
        >
          <div className="flex items-start gap-4">
            {/* Reorder controls */}
            <div className="flex flex-col gap-1 pt-2">
              <button
                onClick={() => onMoveStep(index, 'up')}
                disabled={index === 0}
                className="p-1 text-gray-300 hover:text-gray-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Move up"
              >
                <GripVertical className="w-4 h-4 rotate-180" />
              </button>
              <button
                onClick={() => onMoveStep(index, 'down')}
                disabled={index === steps.length - 1}
                className="p-1 text-gray-300 hover:text-gray-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Move down"
              >
                <GripVertical className="w-4 h-4" />
              </button>
            </div>

            {/* Step number */}
            <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-[#00674a] text-white flex items-center justify-center text-lg font-bold shadow-md">
              {index + 1}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0 space-y-3">
              <textarea
                value={step.description}
                onChange={(e) => onStepChange(index, 'description', e.target.value)}
                placeholder="Describe this step..."
                rows={2}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00674a]/20 focus:border-[#00674a] outline-none transition-all text-gray-900 resize-none bg-white"
              />

              <div className="flex items-center gap-3 flex-wrap">
                <button
                  onClick={() => onStepChange(index, 'isSafetyCritical', !step.isSafetyCritical)}
                  className={`
                    inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all
                    ${
                      step.isSafetyCritical
                        ? 'bg-red-100 text-red-700 border border-red-200'
                        : 'bg-gray-100 text-gray-500 border border-gray-200 hover:bg-gray-200'
                    }
                  `}
                >
                  {step.isSafetyCritical ? (
                    <ShieldAlert className="w-3.5 h-3.5" />
                  ) : (
                    <Shield className="w-3.5 h-3.5" />
                  )}
                  Safety Critical
                </button>

                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <input
                    type="number"
                    min="0"
                    value={step.expectedDuration ?? ''}
                    onChange={(e) =>
                      onStepChange(
                        index,
                        'expectedDuration',
                        e.target.value ? parseInt(e.target.value) : null
                      )
                    }
                    placeholder="Duration"
                    className="w-20 px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#00674a]/20 focus:border-[#00674a] outline-none transition-all"
                  />
                  <span className="text-xs text-gray-400">sec</span>
                </div>
              </div>
            </div>

            {/* Delete button */}
            <button
              onClick={() => onRemoveStep(index)}
              className="flex-shrink-0 p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
              title="Remove step"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </div>
        </div>
      ))}

      {/* Add step button */}
      <button
        onClick={onAddStep}
        className="w-full py-4 border-2 border-dashed border-gray-200 rounded-xl text-gray-500 font-semibold hover:border-[#00674a] hover:text-[#00674a] hover:bg-[#c5e8c2]/10 transition-all flex items-center justify-center gap-2"
      >
        <Plus className="w-5 h-5" />
        Add Step
      </button>

      {/* Save footer */}
      <div className="pt-4 border-t border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {saveStatus === 'saving' && (
            <>
              <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
              <span className="text-sm text-gray-500">Saving...</span>
            </>
          )}
          {saveStatus === 'saved' && (
            <>
              <CheckCircle2 className="w-4 h-4 text-green-500" />
              <span className="text-sm text-green-600 font-medium">Saved!</span>
            </>
          )}
          {saveStatus === 'error' && (
            <>
              <XCircle className="w-4 h-4 text-red-500" />
              <span className="text-sm text-red-600 font-medium">Save failed</span>
            </>
          )}
        </div>

        <button
          onClick={onSave}
          disabled={!hasChanges || saveStatus === 'saving'}
          className="px-6 py-2.5 bg-[#00674a] text-white font-semibold rounded-xl hover:bg-[#005540] disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 shadow-lg shadow-[#00674a]/20"
        >
          <Save className="w-4 h-4" />
          Save Changes
        </button>
      </div>
    </div>
  );
}
